import type { RoleClaim } from "@/types/clm-web";

/**
 * Frontend mirror of the CLMS-116 admin role catalog and tier semantics
 * (clm-web-service-auth src/application/admin-role-tiers.ts and the
 * clm-web-db-schema seed migration). The catalog is deliberately code-defined
 * on both sides: role→operation mapping is a code-reviewed deny-by-default
 * allow-list, never DB permission tables, and the contract exposes no catalog
 * endpoint. Everything here is a UI affordance hint only — the server stays
 * authoritative for every authorization decision (FR-003).
 */

export type AdminRoleTier = "operator" | "escalation";

export interface AdminCatalogRole {
  /** Exact seeded role name carried by grant payloads and assignments. */
  roleName: string;
  /** Projected role claim for the same role (role-access normalization). */
  claim: RoleClaim;
  tier: AdminRoleTier;
  /**
   * Operator tier carries the People administration/adjudication permissions
   * (server grants: People CRUD, pending-application adjudication). D17: the
   * Courses Admin tier admits into the admin realm with no People permissions.
   */
  peopleAccess: boolean;
  description: string;
}

/**
 * The carried admin role catalog for the identities surfaces (legacy prod
 * census 2026-08-06; Courses Admin added per D17; Financial Controller and
 * Content Manager per 011-CLMS-237 CR-006). Operator tiers admit into
 * the admin realm; Owner and Super Admin are escalation modifiers that never
 * admit on their own.
 */
export const ADMIN_ROLE_CATALOG: readonly AdminCatalogRole[] = [
  {
    roleName: "Admin",
    claim: "admin",
    tier: "operator",
    peopleAccess: true,
    description: "Full People administration plus admin identity management.",
  },
  {
    roleName: "Admin Support Staff",
    claim: "admin_support_staff",
    tier: "operator",
    peopleAccess: true,
    description: "People administration and adjudication.",
  },
  {
    roleName: "Membership Coordinator",
    claim: "membership_coordinator",
    tier: "operator",
    peopleAccess: true,
    description: "People administration and adjudication.",
  },
  {
    roleName: "Courses Admin",
    claim: "courses_admin",
    tier: "operator",
    peopleAccess: false,
    description: "Courses administration; no People permissions.",
  },
  {
    roleName: "Financial Controller",
    claim: "financial_controller",
    tier: "operator",
    peopleAccess: false,
    description: "Content management; no People, courses or finance permissions.",
  },
  {
    roleName: "Content Manager",
    claim: "content_manager",
    tier: "operator",
    peopleAccess: false,
    description: "Content management; no People, courses or finance permissions.",
  },
  {
    roleName: "Owner",
    claim: "owner",
    tier: "escalation",
    peopleAccess: false,
    description: "Escalation modifier for restricted grants; never admits on its own.",
  },
  {
    roleName: "Super Admin",
    claim: "super_admin",
    tier: "escalation",
    peopleAccess: false,
    description: "Escalation modifier for restricted grants; never admits on its own.",
  },
];

const ADMIN_OPERATOR_ROLES: readonly AdminCatalogRole[] = ADMIN_ROLE_CATALOG.filter(
  (role) => role.tier === "operator",
);

/**
 * Operator-tier role claims that admit into the admin realm (shell, landing
 * dashboard). Mirrors the server admission rule: escalation modifiers
 * (Owner/Super Admin) never admit on their own. Realm admission is NOT People
 * access — see PEOPLE_OPERATOR_ROLE_CLAIMS.
 */
export const ADMIN_OPERATOR_ROLE_CLAIMS: readonly RoleClaim[] = ADMIN_OPERATOR_ROLES.map(
  (role) => role.claim,
);

/**
 * Operator-tier role claims that carry People administration and adjudication
 * (the People surface, its routes and dashboard tile). Excludes Courses Admin
 * (D17), which admits to the realm but holds no People permissions.
 */
export const PEOPLE_OPERATOR_ROLE_CLAIMS: readonly RoleClaim[] = ADMIN_OPERATOR_ROLES.filter(
  (role) => role.peopleAccess,
).map((role) => role.claim);

/** Operator tiers that only reach content management (011-CLMS-237 CR-006). */
const CONTENT_ONLY_ROLE_CLAIMS: readonly RoleClaim[] = ["financial_controller", "content_manager"];

/**
 * Operator-tier role claims admitted to the admin-realm financial module (staff
 * invoices, its routes, tile and navigation entry): every operator tier except
 * the content-only ones, so finance access is unchanged by CLMS-237.
 */
export const ADMIN_FINANCE_ROLE_CLAIMS: readonly RoleClaim[] = ADMIN_OPERATOR_ROLE_CLAIMS.filter(
  (claim) => !CONTENT_ONLY_ROLE_CLAIMS.includes(claim),
);

/**
 * Claims of the CMS editor roles (`CMS_EDITOR_ROLES` in clm-web-service-cms):
 * Admin, Owner, Financial Controller, Content Manager. A UI hint for the
 * "Manage content" entry; the CMS decides on the token's roles.
 */
export const CMS_EDITOR_ROLE_CLAIMS: readonly RoleClaim[] = [
  "admin",
  "owner",
  "financial_controller",
  "content_manager",
];

/**
 * Human-readable Oxford list of the operator-tier role names, derived from the
 * catalog so UI copy never carries its own role list (e.g. "Admin, Admin
 * Support Staff, Membership Coordinator, or Courses Admin").
 */
export function formatAdminOperatorRoleNames(): string {
  const names = ADMIN_OPERATOR_ROLES.map((role) => role.roleName);
  if (names.length <= 2) {
    return names.join(" or ");
  }
  return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
}

/** Person-domain role labels whose grant requires an escalation modifier. */
export const RESTRICTED_PERSON_ROLE_LABELS = ["Staff Counsel", "Insurance Company Fellow"] as const;

export const ESCALATION_REQUIREMENT_TEXT = "Requires Owner or Super Admin.";
export const ADMIN_REQUIREMENT_TEXT = "Requires the Admin role.";
export const OWNER_REQUIREMENT_TEXT = "Requires the Owner role.";

function normalizeRoleLabel(value: string): string {
  return value.trim().toLowerCase();
}

/** Actor holds an escalation modifier (Owner or Super Admin) projection. */
export function hasEscalationRole(roleClaims: readonly RoleClaim[]): boolean {
  return roleClaims.includes("owner") || roleClaims.includes("super_admin");
}

/**
 * Admin, Owner or Super Admin: auth's invoice-management roles, which its
 * staff projection admits to every financial operation class (e.g.
 * `invoices.notice-resend` for notice resends, `invoices.write` for pricing
 * overrides).
 */
export function hasInvoiceManagementRole(roleClaims: readonly RoleClaim[]): boolean {
  return roleClaims.includes("admin") || hasEscalationRole(roleClaims);
}

/**
 * Identity management (grant and role lifecycle) admission requires the Admin
 * operator role — the other operator tiers cannot even read the identities
 * surfaces, and escalation modifiers never admit alone.
 */
export function canManageAdminIdentities(roleClaims: readonly RoleClaim[]): boolean {
  return roleClaims.includes("admin");
}

/**
 * Assignment-tier rule for granting or transitioning one catalog role:
 * assigning Owner requires Owner; assigning Super Admin requires Owner or
 * Super Admin; every other assignment requires Admin.
 */
export function canAssignAdminRole(roleName: string, roleClaims: readonly RoleClaim[]): boolean {
  const normalized = normalizeRoleLabel(roleName);
  if (normalized === "owner") {
    return roleClaims.includes("owner");
  }
  if (normalized === "super admin") {
    return hasEscalationRole(roleClaims);
  }
  return roleClaims.includes("admin");
}

/** Explanatory text for a tier-disabled assignment control. */
export function adminRoleAssignmentRequirementText(roleName: string): string {
  const normalized = normalizeRoleLabel(roleName);
  if (normalized === "owner") {
    return OWNER_REQUIREMENT_TEXT;
  }
  if (normalized === "super admin") {
    return ESCALATION_REQUIREMENT_TEXT;
  }
  return ADMIN_REQUIREMENT_TEXT;
}

/** Restricted person-role grants require an escalation modifier (Owner/Super Admin). */
export function isRestrictedPersonRoleLabel(label: string): boolean {
  const normalized = normalizeRoleLabel(label);
  return RESTRICTED_PERSON_ROLE_LABELS.some(
    (restricted) => normalizeRoleLabel(restricted) === normalized,
  );
}
